// Canonical audience detail/edit drawer. Every write maps directly to an
// audience API mutation; unavailable analytics are not synthesized.

import { ResponsiveOverlay, useLeaveOverlay } from '@/components/responsive-overlay';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChannelPill, StatusDot } from '@/components/primitives';
import { campaignKindOf } from '@/lib/campaign-status';
import { Icons } from '@/components/primitives';
import { MutationErrorLine } from '@/components/error-panel';
import { FailureNotice } from '@/components/failure-notice';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import {
  useAudiences,
  useAudienceSyncStatus,
  useCampaigns,
  usePatchAudience,
  useProductions,
} from '@/api/hooks';
import { ago } from '@/lib/datetime';
import { audienceRefreshView } from '@/lib/audience-refresh-view';
import { audienceSyncStateWords } from '@/lib/audience-sync-words';
import { lookalikeSeedDependents } from '@/lib/lookalike-seed-dependents';
import {
  isManagedAudience,
  recentBuyersNoticeLine,
} from '@/lib/recent-buyers';
import {
  audienceTypeFromVisualType,
  resolveVisualAudienceRules,
  visualGroupsFromCanonical,
  visualTypeFromAudienceType,
} from '@/lib/audience-visual-rules';
import type { EncodedAudienceRules } from '@/lib/audience-rule-codec';
import type { Audience, Campaign } from '@/api/types';
import { RulesEditor } from './audience-rules-editor';

/** A row of the audiences list: the server's audience, flagged once it was archived in this
 * browser session (the list read returns active audiences only, so the flag is the list's own). */
export type ListedAudience = Audience & { archived?: boolean };

export interface AudienceDetailDrawerProps {
  audience: ListedAudience;
  onClose: () => void;
  /** The campaigns to list as users of this audience; left out, the drawer reads them. */
  campaigns?: readonly Campaign[];
  productionCount?: number;
  onDuplicate?: (audience: ListedAudience) => void;
  onArchive?: (audience: ListedAudience) => Promise<void>;
  onRestore?: (audience: ListedAudience) => Promise<void>;
  onRefresh?: (audience: ListedAudience) => Promise<unknown>;
  actionError?: Error | null;
  actionPending?: boolean;
}

function AudienceDetailDrawer({
  audience,
  onClose,
  campaigns,
  productionCount = 0,
  onDuplicate,
  onArchive,
  onRestore,
  onRefresh,
  actionError = null,
  actionPending = false,
}: AudienceDetailDrawerProps) {
  const patchAudience = usePatchAudience();
  const { data: campaignsFromHook = [] } = useCampaigns();
  const { data: productions = [] } = useProductions();
  const { data: audiences = [] } = useAudiences();
  // An archived audience has no live sync, and the route answers 404 for one (`GET
  // /audiences/{id}/sync-status` without a revision), so the poll is off while it is archived —
  // and from the moment Archive is confirmed: the archive's success drops the cached status
  // while this drawer is still open, and an enabled poll rebuilt and refetched it into that 404
  // (the parallel crawl, 2026-10-18). A failed archive turns the poll back on.
  const [archiving, setArchiving] = useState(false);
  const { data: syncStatus } = useAudienceSyncStatus(audience.id, { enabled: !audience.archived && !archiving });
  // Pressing Refresh writes `next_refresh_at` and nothing else, so without
  // this the drawer looks identical before and after the click. Recomputed on
  // each render off the polled status; no timer, because the only transitions
  // that matter arrive with the next poll anyway.
  const refreshView = audienceRefreshView(syncStatus, Date.now());
  const allCampaigns = campaigns || campaignsFromHook;
  const visualType = visualTypeFromAudienceType(audience.audience_type);
  const managed = isManagedAudience(audience);

  const [name, setName] = useState(audience.name);
  const [desc, setDesc] = useState(audience.description || '');
  const [groups, setGroups] = useState(() => visualGroupsFromCanonical(audience.rules));
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const archiveButton = useRef<HTMLButtonElement>(null);
  const required = useRequiredFeedback();

  useEffect(() => {
    setName(audience.name);
    setDesc(audience.description || '');
    setGroups(visualGroupsFromCanonical(audience.rules));
    setArchiveConfirm(false);
    setArchiving(false);
    required.clearAll();
    patchAudience.reset();
  }, [audience.id, audience.name, audience.description, audience.rules]);

  useEffect(() => {
    if (!archiveConfirm) return undefined;
    const close = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('.ad-confirm-pop') && !target?.closest('.ad-archive-btn')) {
        setArchiveConfirm(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [archiveConfirm]);

  const canonical = useMemo((): { value: EncodedAudienceRules | null; error: unknown } => {
    try {
      return {
        value: resolveVisualAudienceRules(groups, { productions, audiences }),
        error: null,
      };
    } catch (error) {
      return { value: null, error };
    }
  }, [audiences, groups, productions]);
  useEffect(() => {
    if (canonical.value) required.clear('audience-drawer-rules');
  }, [canonical.value]);
  const initialGroups = useMemo(
    () => visualGroupsFromCanonical(audience.rules),
    [audience.rules],
  );
  const definitionDirty = JSON.stringify(groups) !== JSON.stringify(initialGroups);
  const dirty = name !== audience.name
    || desc !== (audience.description || '')
    || definitionDirty;
  // Only a definition edit can invalidate a dependent lookalike's fit: the fit is
  // joined to the seed's revision, and a rename does not bump one. Warning on a
  // rename would train the operator to dismiss the warning that matters.
  const seedDependents = useMemo(
    () => (definitionDirty ? lookalikeSeedDependents(audience.id, audiences) : []),
    [definitionDirty, audience.id, audiences],
  );
  const usedBy = allCampaigns
    .filter((campaign) => campaign.audience_id === audience.id)
    .slice(0, 6);
  const productionNames = useMemo(
    () => new Map(productions.map((production) => [production.id, production.name])),
    [productions],
  );

  const save = async () => {
    // `managed` here, not only on the button: the API refuses a managed patch
    // with a 409 anyway, and a request that can only ever fail should not be
    // sent from a form the operator was not offered.
    if (!dirty || audience.archived || managed || patchAudience.isPending) return;
    const valid = required.report([
      !name.trim() && {
        key: 'audience-drawer-name',
        message: 'Enter an audience name before saving.',
      },
      definitionDirty && !canonical.value && {
        key: 'audience-drawer-rules',
        message: canonical.error instanceof Error
          ? canonical.error.message
          : 'Fix the audience rules before saving.',
      },
    ]);
    if (!valid) return;
    // A rename or a new description sends no definition: the stored rules stay exactly as
    // they are, root shape included, instead of being re-encoded from the rows.
    const definition = definitionDirty ? canonical.value : null;
    if (definitionDirty && !definition) return;
    try {
      await patchAudience.mutateAsync({
        id: audience.id,
        patch: {
          name: name.trim(),
          description: desc.trim() || null,
          ...(definition
            ? { audience_type: audienceTypeFromVisualType(visualType), rules: definition.rules }
            : {}),
        },
      });
      onClose();
    } catch {
      // MutationErrorLine renders the API error without dismissing edits.
    }
  };

  return (
    <>

      <ResponsiveOverlay label="audience drawer details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div style={{flex:1,minWidth:0}}>
            <div className="muted mono audience-drawer-eyebrow" style={{fontSize:10,letterSpacing:'.1em'}}>
              AUDIENCE · {audience.audience_type.toUpperCase()}
            </div>
            <RequiredField
              fieldKey="audience-drawer-name"
              message={required.messages['audience-drawer-name']}
              attempt={required.attempt}
            >
              <input
                value={name}
                onChange={(event) => {
                  const next = event.target.value;
                  setName(next);
                  if (next.trim()) required.clear('audience-drawer-name');
                }}
                className="input ad-name-input"
                aria-label="Audience name"
                spellCheck="false"
                disabled={audience.archived || managed}
              />
            </RequiredField>
            <div className="flex items-center gap-3" style={{marginTop:6,flexWrap:'wrap'}}>
              <span className="tag">{visualType}</span>
              {managed && (
                <span
                  className="tag"
                  title="Created and maintained by SAM from Admin → Config. Edits here are refused because the next reconciliation would overwrite them."
                  style={{background:'var(--accent)',color:'var(--bg)',borderColor:'transparent',letterSpacing:'.12em'}}
                >MANAGED BY SAM</span>
              )}
              {audience.archived && (
                <span className="tag" style={{background:'var(--ink-2)',color:'var(--bg)',borderColor:'transparent',letterSpacing:'.12em'}}>ARCHIVED</span>
              )}
              <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em',textTransform:'uppercase'}}>
                {/* "measured", not "matched": for a materialized audience this
                    column is a real person count written back by the
                    materializer, not a match rate and not an estimate. */}
                {audience.estimated_size != null ? `${audience.estimated_size.toLocaleString()} people` : 'size pending'}
              </span>
              <span className="muted mono desktop-only" style={{fontSize:10.5,letterSpacing:'.06em',textTransform:'uppercase'}}>
                {productionCount} production{productionCount === 1 ? '' : 's'}
              </span>
              <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em',textTransform:'uppercase'}}>
                Updated {ago(audience.updated_at)}
              </span>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{fontSize:18,padding:'4px 10px'}}>×</button>
        </div>

        <div className="drawer-body">
          <div className="field-lbl mb-2">Current state</div>
          <div className="grid-4 mb-3" style={{gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
            {[
              ['Measured size', audience.estimated_size?.toLocaleString() ?? '—'],
              ['Sync status', audienceSyncStateWords(audience.sync?.status, audience.sync?.target) ?? 'Not started'],
              ['Sync attempts', audience.sync?.attempts ?? 0],
              ['Size measured', audience.size_estimated_at ? ago(audience.size_estimated_at) : '—'],
            ].map(([label, value]) => (
              <div key={label} className="panel" style={{padding:'10px 12px',background:'var(--bg)'}}>
                <div className="muted mono" style={{fontSize:9.5,letterSpacing:'.1em',textTransform:'uppercase'}}>{label}</div>
                <div style={{fontFamily:'var(--font-display)',fontSize:18,fontWeight:800,marginTop:2,overflow:'hidden',textOverflow:'ellipsis'}}>{value}</div>
              </div>
            ))}
          </div>

          <div className="field-lbl mb-2">Destinations</div>
          <div className="panel" style={{padding:'4px 0',marginBottom:14}}>
            {[
              {
                key: 'meta',
                label: 'META',
                status: audienceSyncStateWords(
                  syncStatus?.meta_status
                    ?? (audience.sync?.target === 'meta' ? audience.sync?.status : null),
                  'meta',
                ),
                externalId: syncStatus?.meta?.audience_id,
                notice: recentBuyersNoticeLine(syncStatus?.meta?.recent_buyers_notice),
                facts: syncStatus?.meta
                  ? ([
                    ['Received', Number(syncStatus.meta.num_received).toLocaleString()],
                    ['Matched', Number(syncStatus.meta.num_matched) < 0 ? 'Pending' : Number(syncStatus.meta.num_matched).toLocaleString()],
                    ['Invalid', Number(syncStatus.meta.num_invalid_entries).toLocaleString()],
                    syncStatus.meta.stage ? ['Stage', String(syncStatus.meta.stage)] : null,
                  ].filter(Boolean) as [string, string][])
                  : [],
                detail: syncStatus?.meta && [
                  `${Number(syncStatus.meta.num_received).toLocaleString()} received`,
                  Number(syncStatus.meta.num_matched) < 0
                    ? 'match count pending'
                    : `${Number(syncStatus.meta.num_matched).toLocaleString()} matched`,
                  `${Number(syncStatus.meta.num_invalid_entries).toLocaleString()} invalid`,
                  syncStatus.meta.stage && `stage ${syncStatus.meta.stage}`,
                ].filter(Boolean).join(' · '),
              },
              {
                key: 'ttd',
                label: 'TTD',
                status: audienceSyncStateWords(
                  syncStatus?.ttd_status
                    ?? (audience.sync?.target === 'ttd' ? audience.sync?.status : null),
                  'ttd',
                ),
                externalId: syncStatus?.ttd?.crm_data_id,
                notice: recentBuyersNoticeLine(syncStatus?.ttd?.recent_buyers_notice),
                facts: syncStatus?.ttd
                  ? ([
                    ['Token lines', Number(syncStatus.ttd.record_count).toLocaleString()],
                    Number(syncStatus.ttd.people_selected) > 0
                      ? ['Reached', `${Number(syncStatus.ttd.people_with_token).toLocaleString()} of ${Number(syncStatus.ttd.people_selected).toLocaleString()}`]
                      : null,
                    syncStatus.ttd.drop_state ? ['Drop', String(syncStatus.ttd.drop_state)] : null,
                    syncStatus.ttd.ttd_audience_id ? ['Audience', String(syncStatus.ttd.ttd_audience_id)] : null,
                  ].filter(Boolean) as [string, string][])
                  : [],
                detail: syncStatus?.ttd && [
                  `${Number(syncStatus.ttd.record_count).toLocaleString()} token lines`,
                  // Reach, in people. This replaced a `matched_count` readout
                  // that was hardcoded to zero on every audience that ever
                  // synced, so the drawer permanently said "0 matched" while
                  // the segment was live. TTD publishes no match counter; this
                  // is our own measurement of how many of the selected people
                  // held a UID2 and therefore reached the provider at all.
                  Number(syncStatus.ttd.people_selected) > 0
                    && `${Number(syncStatus.ttd.people_with_token).toLocaleString()} of `
                      + `${Number(syncStatus.ttd.people_selected).toLocaleString()} people`
                      + ` (${Math.round(
                        (Number(syncStatus.ttd.people_with_token)
                          / Number(syncStatus.ttd.people_selected)) * 100,
                      )}%)`,
                  syncStatus.ttd.drop_state && `drop ${syncStatus.ttd.drop_state}`,
                  syncStatus.ttd.ttd_audience_id && `audience ${syncStatus.ttd.ttd_audience_id}`,
                ].filter(Boolean).join(' · '),
              },
            ].map((destination, index) => (
              <div className="stack-phone audience-destination" key={destination.key} style={{display:'grid',gridTemplateColumns:'56px 150px 1fr',gap:10,padding:'10px 14px',borderTop:index > 0 ? '1px solid var(--line-2)' : 'none',alignItems:'baseline',fontSize:12}}>
                <span className="mono audience-destination-label" style={{fontSize:10.5,letterSpacing:'.1em'}}>{destination.label}</span>
                <span className="audience-destination-status" style={{fontSize:12,fontWeight:600}}>{destination.status ?? '—'}</span>
                <div style={{minWidth:0}}>
                  {/* Desktop keeps the one-line summary; the phone reads the same facts as a labelled grid. */}
                  <div className="muted desktop-only" style={{fontSize:11,overflowWrap:'anywhere'}}>{destination.detail || 'No platform detail yet.'}</div>
                  <dl className="audience-destination-facts phone-only">
                    {destination.facts.length === 0 && <div><dd className="muted">No platform detail yet.</dd></div>}
                    {destination.facts.map(([k, v]) => (
                      <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
                    ))}
                  </dl>
                  {/* batch-worker's notice: a held refresh, which the leg's own
                      status does not say — a held refresh leaves it reading
                      synced. One neutral sentence, no hover. Display only: the
                      same line on desktop and phone, and nothing gates on it. */}
                  {destination.notice && (
                    <div
                      className="audience-destination-notice"
                      style={{fontSize:11,marginTop:4,lineHeight:1.5,color:'var(--warn)',overflowWrap:'anywhere'}}
                    >
                      {destination.notice.text}
                    </div>
                  )}
                  {destination.externalId && (
                    <div className="muted mono audience-destination-id" style={{fontSize:10,marginTop:2,overflowWrap:'anywhere'}}>
                      <span className="phone-only audience-destination-id-label">Container</span>{destination.externalId}
                      <button type="button" className="btn btn-sm phone-only audience-destination-copy" aria-label="Copy container id" onClick={() => void navigator.clipboard?.writeText(String(destination.externalId))}>{Icons.copy}</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {/* The journals keep last_error until a leg proves synced, so a
                healthy retry still carries its previous failure text — only
                surface it while a leg is actually failed. */}
            {syncStatus?.last_error
              && (syncStatus?.meta_status === 'failed' || syncStatus?.ttd_status === 'failed') && (
              <div className="muted" style={{padding:'8px 14px 12px',fontSize:11,borderTop:'1px solid var(--line-2)',overflowWrap:'anywhere'}}>
                <FailureNotice dense announce={false} failure={syncStatus.failure} fallback={syncStatus.last_error} />
              </div>
            )}
          </div>

          <div className="field-lbl mb-2">Description</div>
          <div className="field" style={{marginBottom:14}}>
            <textarea
              className="input"
              rows={2}
              value={desc}
              onChange={(event) => setDesc(event.target.value)}
              placeholder="What this audience is for, when to use it…"
              disabled={audience.archived || managed}
            />
          </div>

          <div className="flex between items-center mb-2">
            <div className="field-lbl" style={{margin:0}}>Rules</div>
            <span className="muted mono" style={{fontSize:10,letterSpacing:'.1em',textTransform:'uppercase'}}>
              {(() => { const n = groups.reduce((count, group) => count + group.rules.length, 0); return `${n} rule${n === 1 ? '' : 's'}`; })()} · {groups.length} group{groups.length === 1 ? '' : 's'}
            </span>
          </div>
          <div style={audience.archived ? {pointerEvents:'none',opacity:.65} : undefined}>
            <RequiredField
              fieldKey="audience-drawer-rules"
              message={required.messages['audience-drawer-rules']}
              attempt={required.attempt}
              surface
            >
              <RulesEditor
                type={visualType}
                groups={groups}
                setGroups={setGroups}
                productions={productions}
                audiences={audiences}
                heading={false}
              />
            </RequiredField>
          </div>
          {!!canonical.error && !required.messages['audience-drawer-rules'] && (
            <div style={{margin:'8px 0 14px'}}><MutationErrorLine error={canonical.error}/></div>
          )}

          <div className="field-lbl mb-2">Refresh</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            <div className="flex between items-center" style={{gap:12}}>
              <div>
                <div style={{fontSize:12.5,fontWeight:600}}>Refresh membership now</div>
                <div className="muted" style={{fontSize:11,marginTop:3}}>
                  Rebuilds who is in this audience and uploads it again to Meta and TTD. It runs in
                  the background; you can close this drawer.
                </div>
              </div>
              <button
                className="btn btn-sm"
                disabled={audience.archived || managed || actionPending || !refreshView.canRefresh}
                onClick={() => void onRefresh?.(audience)}
              >
                {actionPending ? 'Working…' : refreshView.state === 'queued' ? 'Scheduled' : 'Refresh now'}
              </button>
            </div>
            {/* The state between the write and the worker noticing it. Queued
                is called out because it is the one an operator would otherwise
                mistake for "nothing happened" and click through. */}
            {refreshView.message && (
              <div
                className="muted"
                style={{
                  fontSize:11,
                  marginTop:10,
                  paddingTop:10,
                  borderTop:'1px solid var(--line)',
                  lineHeight:1.5,
                  color: refreshView.state === 'queued' ? 'var(--accent)' : undefined,
                }}
              >
                {refreshView.state === 'queued' && <span className="pulse" style={{marginRight:6}}/>}
                {refreshView.message}
              </div>
            )}
          </div>

          <div className="field-lbl mb-2">Latest persisted snapshot</div>
          <div className="panel" style={{padding:10,marginBottom:14}}>
            <div className="stack-phone" style={{display:'grid',gridTemplateColumns:'110px 1fr 110px 110px',gap:10,padding:'6px 4px',fontSize:11.5,alignItems:'baseline'}}>
              <div className="mono" style={{fontSize:10.5}}>Current</div>
              <div>{audienceSyncStateWords(audience.sync?.status, audience.sync?.target) ?? 'Definition saved'}</div>
              <div className="muted mono" style={{fontSize:10.5,textAlign:'right'}}>{audience.estimated_size?.toLocaleString() ?? '—'}</div>
              <div className="muted mono" style={{fontSize:10.5,textAlign:'right'}}>{ago(audience.updated_at)}</div>
            </div>
          </div>

          <div className="field-lbl mb-2">
            Campaigns using this audience
            <span className="muted mono" style={{fontSize:10,letterSpacing:'.06em',marginLeft:8}}>({usedBy.length})</span>
          </div>
          <div className="panel" style={{padding:'4px 0',marginBottom:14}}>
            {usedBy.length === 0 && (
              <div className="muted" style={{padding:'14px 16px',fontSize:12,fontStyle:'italic'}}>Not used in any campaigns yet.</div>
            )}
            {usedBy.map((campaign, index) => (
              <div className="stack-phone" key={campaign.id} style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 140px',gap:10,padding:'10px 14px',borderTop:index > 0 ? '1px solid var(--line-2)' : 'none',alignItems:'center',fontSize:12}}>
                <div style={{minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  <span style={{color:'var(--ink)',fontWeight:600}}>{campaign.name}</span>
                </div>
                <ChannelPill channel={campaign.channel}/>
                <StatusDot s={campaignKindOf(campaign)}/>
                <span
                  className="muted"
                  style={{fontSize:11,textAlign:'right',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                  title={productionNames.get(campaign.production_id)}
                >
                  {productionNames.get(campaign.production_id) ?? 'Unknown production'}
                </span>
              </div>
            ))}
          </div>

          <div className="field-lbl mb-2">Properties</div>
          <div className="panel" style={{padding:'4px 12px',marginBottom:8}}>
            {[
              ['ID', audience.id],
              ['Type', audience.audience_type],
              ['Created', ago(audience.created_at)],
              ['Updated', ago(audience.updated_at)],
              ['Sync targets', [
                (syncStatus?.meta_status || syncStatus?.meta || audience.sync?.target === 'meta') && 'meta',
                (syncStatus?.ttd_status || syncStatus?.ttd || audience.sync?.target === 'ttd') && 'ttd',
              ].filter(Boolean).join(' · ') || '—'],
            ].map(([label, value]) => (
              <div key={label} style={{display:'grid',gridTemplateColumns:'120px 1fr',padding:'8px 0',borderBottom:'1px solid var(--line-2)',fontSize:12}}>
                <div className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em',textTransform:'uppercase'}}>{label}</div>
                <div style={{overflowWrap:'anywhere'}}>{value}</div>
              </div>
            ))}
          </div>

          {seedDependents.length > 0 && (
            // Shown before the save, not after: this is a cost to accept, not an
            // error to recover from. Editing a seed is allowed precisely because
            // nothing is minted and no provider container moves — but the dependent
            // lookalikes stop having a published fit until the nightly refit lands,
            // and the operator should not discover that from a failed refresh.
            <div
              className="panel"
              style={{marginTop:10,padding:'10px 14px',fontSize:12,borderColor:'var(--warn, var(--line))'}}
            >
              <div style={{fontWeight:600,marginBottom:4}}>
                {seedDependents.length === 1
                  ? '1 lookalike is built from this audience'
                  : `${seedDependents.length} lookalikes are built from this audience`}
              </div>
              <div className="muted" style={{lineHeight:1.5}}>
                {seedDependents.map((dependent) => dependent.name).join(', ')}
                {' — saving a new definition re-fits '}
                {seedDependents.length === 1 ? 'it' : 'them'}
                {' overnight. Until that finishes '}
                {seedDependents.length === 1 ? 'it has' : 'they have'}
                {' no published fit, so a refresh of '}
                {seedDependents.length === 1 ? 'that lookalike' : 'those lookalikes'}
                {' will fail in the meantime. Campaigns already delivering keep the '}
                {'audience they are targeting; nothing is detached.'}
              </div>
            </div>
          )}
          {(patchAudience.error || actionError) && (
            <div style={{marginTop:10}}>
              <MutationErrorLine error={patchAudience.error || actionError}/>
            </div>
          )}
        </div>

        <div className="drawer-foot" style={{position:'relative'}}>
          <DuplicateButton onDuplicate={onDuplicate ? () => onDuplicate(audience) : undefined}/>
          {audience.archived ? (
            <button className="btn btn-sm" disabled={actionPending} onClick={() => void onRestore?.(audience)} title="Restore this audience to active use">
              {actionPending ? 'Working…' : 'Restore'}
            </button>
          ) : (
            <button
              ref={archiveButton}
              className="btn btn-sm ad-archive-btn"
              disabled={actionPending}
              onClick={() => setArchiveConfirm((current) => !current)}
              title="Archive this audience"
            >
              Archive
            </button>
          )}
          {archiveConfirm && (
            <ArchiveConfirmPop
              audience={audience}
              usageCount={usedBy.length}
              onCancel={() => setArchiveConfirm(false)}
              onConfirm={() => {
                setArchiveConfirm(false);
                setArchiving(true);
                void (onArchive?.(audience) ?? Promise.resolve()).finally(() => setArchiving(false));
              }}
            />
          )}
          <button
            className="btn btn-primary"
            style={{marginLeft:'auto'}}
            onClick={() => void save()}
            disabled={!dirty || audience.archived || managed || patchAudience.isPending}
            title={audience.archived ? 'Archived audiences are read-only — restore first' : !dirty ? 'No changes to save' : 'Save and re-sync this audience'}
          >
            {Icons.check} {patchAudience.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}

/** Duplicate opens the wizard, so it leaves the drawer's history entry first (`useLeaveOverlay`):
 * on a phone the entry's own Back would otherwise land after the navigation and undo it. */
function DuplicateButton({ onDuplicate }: { onDuplicate?: () => void }) {
  const leave = useLeaveOverlay();
  return (
    <button
      className="btn btn-sm"
      onClick={() => { if (onDuplicate) leave(onDuplicate); }}
      title="Create a copy from this canonical definition"
    >
      Duplicate
    </button>
  );
}

interface ArchiveConfirmPopProps {
  audience: Pick<Audience, 'name'>;
  usageCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}

function ArchiveConfirmPop({ audience, usageCount, onCancel, onConfirm }: ArchiveConfirmPopProps) {
  return (
    <div className="ad-confirm-pop" onClick={(event) => event.stopPropagation()}>
      <div className="ad-confirm-arrow"/>
      <div className="ad-confirm-title">Archive this audience?</div>
      <div className="ad-confirm-body">
        {usageCount > 0 ? (
          <><b>{usageCount}</b> campaign{usageCount === 1 ? '' : 's'} currently reference <b>{audience.name}</b>. Existing campaign records are not deleted.</>
        ) : (
          <><b>{audience.name}</b> is not referenced by a campaign. Its definition can be restored during this session.</>
        )}
      </div>
      <div className="ad-confirm-actions">
        <button className="btn btn-sm btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn btn-sm ad-confirm-go" onClick={onConfirm}>Archive</button>
      </div>
    </div>
  );
}

export { AudienceDetailDrawer };
